import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import axios from 'axios';

import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { UpsellModalLayout } from '../upsell-modal-layout/upsell-modal-layout';
import { FeatureDescription } from '../feature-upsell-modal/feature-description';
import { TrialUpsellFooter } from './trial-upsell-footer';
import { UpsellModalImage } from '../modal-svgs/upsell-modal-image';
import { useLocalizedPlanName } from '../../../pages/settings/payment-and-subscription/components/plan-name-helper';
import { useGlobalModalLaunchContext } from '../../../contexts/modal-context/global-modal-context';
import { BillingPlanIds } from '../../../enums/billing-plan-ids';
import {
  BannerSourceToFeatureCode,
  TrialTiers,
  TriableBillingPlanTiersToPlanCodes,
  type BannerSource,
  type TrialTier,
} from '../../plan-features-trial-upsell-banner/constants';
import { getSeller } from '../../../store/seller/seller.actions';
import { useSendMetrics } from '../../../hooks/use-send-metrics';
import {
  PREMIUM_TRIAL_OFFER_MODAL_OPENED,
  PREMIUM_TRIAL_OFFER_START_TRIAL_CLICKED,
  PREMIUM_TRIAL_OFFER_START_TRIAL_SUCCESS,
  PREMIUM_TRIAL_OFFER_UPGRADE_NOW_CLICKED,
} from '../../../constants/analytics-events';

import { messages } from './messages';

import upsellModalStyles from '../upsell-modal.module.less';

/** Maps trial tiers to their corresponding billing plan IDs */
const TrialTierToBillingPlanId: Record<TrialTier, number> = {
  [TrialTiers.Standard]: BillingPlanIds.Standard2025Q2,
  [TrialTiers.Premium]: BillingPlanIds.Premium2025Q2,
};

/** Maps trial tiers to their plan names for localization lookup */
const TrialTierToPlanName: Record<TrialTier, string> = {
  [TrialTiers.Standard]: 'Standard',
  [TrialTiers.Premium]: 'Premium',
};

type TrialUpsellModalProps = {
  source: BannerSource;
  tier: TrialTier;
  onClose: () => void;
};

export const TrialUpsellModal = ({ source, tier, onClose }: TrialUpsellModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl();
  const dispatch = useDispatch();
  // @ts-expect-error - context is JS with null default, but guaranteed to exist within GlobalModalLaunchContextProvider
  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();

  const upsellModalContentCode = BannerSourceToFeatureCode[source];
  const billingPlanId = TrialTierToBillingPlanId[tier];
  const planName = useLocalizedPlanName(TrialTierToPlanName[tier]);

  const sendMetrics = useSendMetrics();

  useEffect(() => {
    sendMetrics(PREMIUM_TRIAL_OFFER_MODAL_OPENED, { tier, source });
  }, [sendMetrics, tier, source]);

  const handleUpgradeNow = useCallback(() => {
    sendMetrics(PREMIUM_TRIAL_OFFER_UPGRADE_NOW_CLICKED, { tier, source });
    openManageSubscriptionDrawer({ selectedPlanId: billingPlanId.toString() });
    onClose();
  }, [sendMetrics, tier, source, openManageSubscriptionDrawer, onClose, billingPlanId]);

  const handleStartTrial = useCallback(async () => {
    sendMetrics(PREMIUM_TRIAL_OFFER_START_TRIAL_CLICKED, { tier, source });
    setIsLoading(true);
    try {
      const targetPlanCode = TriableBillingPlanTiersToPlanCodes[tier];
      await axios.post('/account/trialPlan', { TargetPlanCode: targetPlanCode });
      sendMetrics(PREMIUM_TRIAL_OFFER_START_TRIAL_SUCCESS, { tier, source });
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.trialStartSuccessTitle} />,
        content: intl.formatMessage(messages.trialStartSuccess, { planName }),
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
      dispatch(getSeller());
      onClose();
    } catch {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.trialStartErrorTitle} />,
        content: intl.formatMessage(messages.trialStartError),
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  }, [sendMetrics, intl, onClose, planName, tier, source, dispatch]);

  const descriptionContent = (
    <FeatureDescription billingPlanId={billingPlanId} planName={planName} upsellModalContentCode={upsellModalContentCode} />
  );

  const footerContent = <TrialUpsellFooter isLoading={isLoading} onStartTrial={handleStartTrial} onUpgradeNow={handleUpgradeNow} />;

  const imageContent = (
    <UpsellModalImage className={upsellModalStyles['purple-background']} upsellModalContentCode={upsellModalContentCode} />
  );

  return (
    <UpsellModalLayout
      descriptionContent={descriptionContent}
      footerContent={footerContent}
      imageContent={imageContent}
      onCancel={onClose}
      onClose={onClose}
    />
  );
};
